import { RSU } from '_lib/api';
import { RootState } from '_store';
import applicationReducer from '_store/application/reducer';
import { dialogSelector, selectionSelector } from '_store/application/selectors';
import { DialogType } from '_store/application/types';
import devicesReducer from '_store/devices/devicesReducers';
import { RsuStatusCode } from '_store/devices/devicesTypes';
import { getRsuStatusFromDiagnostic } from '_store/devices/utils';
import L, { Map } from 'leaflet';
import React, { FC, ReactElement, useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';

import { canInfoViewBeOpened } from '../../../InfoView/utils';
import { encodeClusterMeta, rsuClusterStatus, useClusterRefresh } from '../../clustering';
import useMarkerPosition from '../../hooks/useMarkerPosition';
import RsuInfoTooltip from '../../InfoTooltips/RsuInfoTooltip';
import { HoveringElementType } from '../../InfoTooltips/types';
import { iconSizePx, rsuStatusIcon } from '../../utils';

const iconSize = new L.Point(iconSizePx, iconSizePx);
const iconAnchor = new L.Point(iconSize.x / 2, iconSize.y);
const tooltipOffset = new L.Point(0, -iconSize.y);

const RsuMarker: FC<{
  device: RSU;
  mapRef: Map;
}> = ({ device, mapRef }): ReactElement => {
  const dispatch = useDispatch();
  const refreshClusters = useClusterRefresh();

  const dialog = useSelector(dialogSelector);
  const selection = useSelector(selectionSelector);
  const canOpenInfo = canInfoViewBeOpened(dialog, selection);

  const diagnostic = useSelector((state: RootState) => state.api.diagnostic[device.id]);
  const iconStatus = getRsuStatusFromDiagnostic(diagnostic) ?? (device.alive ? RsuStatusCode.UP : undefined);

  const markerPosition = useMarkerPosition(device?.location?.coordinates);

  const clusterAttribution = useMemo(
    () =>
      encodeClusterMeta({
        group: 'rsu',
        status: rsuClusterStatus(device.alive, iconStatus),
      }),
    [device.alive, iconStatus],
  );

  const skipFirstRefresh = useRef(true);
  useEffect(() => {
    if (skipFirstRefresh.current) {
      skipFirstRefresh.current = false;
      return;
    }
    refreshClusters();
  }, [clusterAttribution, refreshClusters]);

  const eventHandlers = useMemo(
    () => ({
      click: () => {
        if (canOpenInfo) {
          dispatch(devicesReducer.actions.deviceRequest(device.id));
          dispatch(
            applicationReducer.actions.infoSelected({
              type: DialogType.SINGLE_DEVICE,
              id: device.id,
            }),
          );
          mapRef.panTo(markerPosition);
        }
      },
    }),
    [mapRef, device, markerPosition, dialog, selection],
  );

  const isRsuSelected =
    dialog &&
    [DialogType.SINGLE_DEVICE, DialogType.DEVICE_PATH].includes(dialog.type) &&
    dialog.id === device.id;

  return (
    <div>
      <Marker
        position={markerPosition}
        icon={
          new L.Icon({
            iconSize,
            iconUrl: rsuStatusIcon(iconStatus),
            iconAnchor,
            className: `marker-event-${isRsuSelected ? 'selected' : 'unselected'}`,
          })
        }
        interactive={true}
        eventHandlers={eventHandlers}
        attribution={clusterAttribution}
      >
        <RsuInfoTooltip
          hoveringElement={{
            type: HoveringElementType.RSU,
            device,
            offset: tooltipOffset,
          }}
        />
      </Marker>
    </div>
  );
};

export default React.memo(RsuMarker);
